import { z } from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import {
  merchantCurrencyCodes,
  type MerchantCurrencyCode,
} from "@/utils/currency"
import type {
  TypedSchemaInput,
  TypedSchemaOutput,
} from "@/utils/typeUtils"
import {
  emailAddressSchema,
  requiredTrimmedString,
  trimmedString,
} from "@/validation/sharedStringFieldSchemas"

const createEditMerchantFormSchema = z
  .object({
    tpReference: requiredTrimmedString(
      "Reference is required"
    ).max(63, "Must be 63 characters or fewer"),
    businessName: requiredTrimmedString(
      "Business name is required"
    ).max(128, "Must be 128 characters or fewer"),
    merchantCategoryCode: trimmedString()
      .max(4)
      .length(4, "MCC must contain exactly 4 characters")
      .regex(/^\d+$/, "MCC must only contain numbers")
      .nullable()
      .or(z.literal("")),
    customerName: requiredTrimmedString(
      "Customer name is required"
    ).max(100, "Must be 100 characters or fewer"),
    customerEmail: emailAddressSchema(
      "Customer email is required"
    ).max(100, "Must be 100 characters or fewer"),
    customerPhone: requiredTrimmedString(
      "Customer phone is required"
    ).max(100, "Must be 100 characters or fewer"),
    addressLine1: trimmedString()
      .max(1024, "Must be 1024 characters or fewer")
      .nullable(),
    addressLine2: trimmedString()
      .max(1024, "Must be 1024 characters or fewer")
      .nullable(),
    currency: z.enum(
      assertIsNonEmptyArray(merchantCurrencyCodes)
    ),
    hasDonationsModule: z.boolean(),
    hasSalesModule: z.boolean(),
    hasBeepaidOrderModule: z.boolean(),
    hasBeepaidGoModule: z.boolean(),

    acquirer: z.union([
      z.literal("barclaycard-elavon"),
      z.literal("evo"),
    ]),
    paxMerchantId: trimmedString()
      .max(15, "Must be 15 characters or fewer")
      .nullable(),
    evoGratuityIsAvailable: z.boolean(),

    cardstreamMerchantId: trimmedString()
      .max(10, "Must be 10 characters or fewer")
      .regex(
        /^\d+$/,
        "Cardstream Merchant ID must only contain numbers"
      )
      .nullable(),
    cardstreamMerchantSecret: trimmedString()
      .max(64, "Must be 64 characters or fewer")
      .nullable(),

    isFromBackbook: z.boolean(),
    isDemoMerchant: z.boolean(),
    demoMenuId: z.number().nullable(),
    isActive: z.boolean(),
  })
  .superRefine((params, ctx) => {
    if (
      params.hasSalesModule ||
      params.hasDonationsModule
    ) {
      if (!params.paxMerchantId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Field is required",
          path: ["paxMerchantId"],
        })
      }
    }
    if (
      params.hasBeepaidOrderModule ||
      params.hasBeepaidGoModule
    ) {
      if (!params.cardstreamMerchantId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Field is required",
          path: ["cardstreamMerchantId"],
        })
      }

      if (!params.cardstreamMerchantSecret) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Field is required",
          path: ["cardstreamMerchantSecret"],
        })
      }
    }
  })

export type CreateEditMerchantFormInput = TypedSchemaInput<
  typeof createEditMerchantFormSchema
>

export type CreateEditMerchantFormOutput =
  TypedSchemaOutput<typeof createEditMerchantFormSchema>

const createEditMerchantFormInitialValues = (
  defaultCurrency: MerchantCurrencyCode
): CreateEditMerchantFormInput => ({
  businessName: "",

  tpReference: "",
  merchantCategoryCode: "",
  currency: defaultCurrency,

  customerName: "",
  customerEmail: "",
  customerPhone: "",

  addressLine1: "",
  addressLine2: "",
  cardstreamMerchantId: null,
  cardstreamMerchantSecret: null,

  acquirer:
    import.meta.env.VITE_ORGANIZATION === "boipa"
      ? "evo"
      : "barclaycard-elavon",
  paxMerchantId: null,
  evoGratuityIsAvailable: false,

  hasSalesModule: true,
  hasDonationsModule: false,
  hasBeepaidOrderModule: false,
  hasBeepaidGoModule: false,

  isFromBackbook: false,
  isDemoMerchant: false,
  demoMenuId: null,
  isActive: true,
})

export const useCreateEditMerchantForm = (
  defaultCurrency: MerchantCurrencyCode
) =>
  useForm({
    validationSchema: toTypedSchema(
      createEditMerchantFormSchema
    ),
    initialValues:
      createEditMerchantFormInitialValues(defaultCurrency),
    keepValuesOnUnmount: true, // Allowed evoGratuityIsAvailable to be unmounted and not be set to `undefined`
  })
